import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { chain, get, isEmpty, isNil, isNumber, reduce, some } from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { CellClassParams } from 'ag-grid-community';

import {
  getFormControl,
  getFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { observableCombineLatest, rx } from '@app/shared/rx';
import {
  IGridRowOrCellClassRule,
  FieldList,
  IFormRules,
  IOrderFormFieldPath,
  IGridFlatInventoryItemVenue,
  IGridFlatInventoryItemVenueTotal,
  IGridInventoryItemVenueTotalFormData,
  SchemaNode,
  WIDGET_COMPONENT,
  XuxGridAction,
  XUX_GRID_ACTIONS,
  SORT_ORDERS,
  IXVIListColumn,
  ITotalCostFieldConfig,
} from '@app/shared/types';

import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { alphanumericComparator, DecimalNumber } from '@app/shared/common';

import { NumberFormatService } from '../number-format';
import { ORDER_GRID_COL_TITLES } from '@app/containers/orders/containers/edit/order-form-col-title-contansts';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { flatTreeDataByHasChildren } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class OrderTotalFormService {
  /**
   * editor grid reference
   */
  public listGrid: XuxEditGridComponent;
  public listGridReady$: Subject<void> = new Subject();
  /**
   * total data
   */
  public totalItemData$: BehaviorSubject<IGridFlatInventoryItemVenueTotal> =
    new BehaviorSubject(null);

  public constructor(
    public numberFormatService: NumberFormatService,
    public i18nService: XVII18nService,
  ) {}

  /**
   * handle when list grid is ready
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleListGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.listGrid = grid;
    this.listGridReady$.next();
    this.listGrid.xuxGridComponent.gridApi.setSortModel([
      { colId: 'name', sort: SORT_ORDERS.ASC },
    ]);
  }

  /**
   * get all columns obs
   *
   * @param formFieldPath form field path mapping, will using this to build field path or define
   * @param isAddAdditionalColumnsObs is need add additional columns observable, if true will combine additional columns
   * @returns all columns obs
   */
  public getTotalGridColumnsObs(
    formFieldPath: IOrderFormFieldPath,
    isAddAdditionalColumnsObs: Observable<boolean>,
  ): Observable<IXVIListColumn[]> {
    const commonColumns: IXVIListColumn[] = [
      {
        title: ORDER_GRID_COL_TITLES.glAccountName,
        path: 'name',
        sortable: true,
        defaultView: true,
        filterParams: {
          quickFilter: true,
        },
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
      },
      {
        title: ORDER_GRID_COL_TITLES.initialItemCount,
        path: formFieldPath.itemCount,
        defaultView: true,
        sortable: true,
        valueFormatter: this.numberFormatService.getNumberFormatter(false),
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        title: ORDER_GRID_COL_TITLES.initialTotalCost,
        path: formFieldPath.totalCost,
        defaultView: true,
        sortable: true,
        valueFormatter: this.numberFormatService.getNumberFormatter(true),
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
        ...COLUMN_ALIGN_RIGHT,
      },
    ];

    const additionalColumns: IXVIListColumn[] = [
      {
        title: ORDER_GRID_COL_TITLES.quantityReceived,
        path: formFieldPath.quantityReceived,
        defaultView: true,
        sortable: true,
        valueFormatter: this.numberFormatService.getNumberFormatter(false),
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        title: ORDER_GRID_COL_TITLES.receivedTotalCost,
        path: formFieldPath.receivedTotalCost,
        defaultView: true,
        sortable: true,
        valueFormatter: this.numberFormatService.getNumberFormatter(true),
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
        ...COLUMN_ALIGN_RIGHT,
      },
    ];

    return isAddAdditionalColumnsObs.pipe(
      rx.map((isAddAdditionalColumns) =>
        isAddAdditionalColumns
          ? commonColumns.concat(additionalColumns)
          : commonColumns,
      ),
    );
  }

  /**
   * get from totals form rules
   *
   * @param formFieldPath form field path mapping, will using this to build field path or define
   * @param isAddAdditionalColumnsObs is need add additional columns observable
   * @returns totals form rules
   */
  public getTotalsFormRules(
    formFieldPath: IOrderFormFieldPath,
    isAddAdditionalColumnsObs: Observable<boolean>,
  ): IFormRules {
    const totalFormFields: FieldList = {
      [formFieldPath.virtualTotalStatus]: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.Common.Totals,
        widgetData: {
          useVisibleItemCountDescription: true,
          showSearch: true,
          multiselect: false,
          orderControls: false,
          disableColumnSelector: true,
          artificialInputCheckForSearchPanel: true,
          columnsObs: this.getTotalGridColumnsObs(
            formFieldPath,
            isAddAdditionalColumnsObs,
          ),
          userGridOptions: {
            rowClassRules: this.getFormRowClassRules(),
          },
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]: this.handleListGridReady.bind(this),
          },
        },
      },
    };

    const virtualSchema: SchemaNode = {
      type: 'object',
      properties: {
        item_totals: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              [formFieldPath.itemCount]: { type: 'number' },
              [formFieldPath.totalCost]: { type: 'number' },
            },
          },
        },
      },
    };

    return {
      fields: totalFormFields,
      virtual: virtualSchema,
    };
  }

  /**
   * get list totals data handler subscription
   *
   * @param form target form
   * @param formFieldPath target form field path mapping
   * @returns list totals data handler subscription
   */
  public getHandleListTotalsDataSubs(
    form: FormGroup,
    formFieldPath: IOrderFormFieldPath,
  ): Subscription {
    return observableCombineLatest([
      getFormControl(form, formFieldPath.inventoryItemVenues).valueChanges,
      this.listGridReady$.pipe(rx.first()),
    ])
      .pipe(
        rx.distinctUntilChanged(),
        rx.map(() =>
          flatTreeDataByHasChildren<IGridFlatInventoryItemVenue>(
            getFormFieldValue(form, formFieldPath.inventoryItemVenues),
          ),
        ),
        rx.map((items) => this.mapItemDataToTotalsData(items, formFieldPath)),
      )
      .subscribe(this.updateListGridData.bind(this));
  }

  /**
   * convert items to totals data
   *
   * @param items inventory items
   * @param formFieldPath form field path mapping
   * @returns totals data
   */
  public mapItemDataToTotalsData(
    items: IGridFlatInventoryItemVenue[],
    formFieldPath: IOrderFormFieldPath,
  ): IGridInventoryItemVenueTotalFormData {
    const normalRows = this.buildNormalRows(items, formFieldPath);
    const pinnedBottomData = this.buildPinnedData(normalRows, formFieldPath);

    return {
      normalRows,
      pinnedBottomData,
    };
  }

  /**
   * get items list received total cost
   * received total cost = received quantity * received unit cost
   *
   * @param itemList target item list
   * @param formFieldPath form field path mapping
   * @returns received total cost
   */
  public getReceivedTotalCost(
    itemList: IGridFlatInventoryItemVenue[],
    formFieldPath: IOrderFormFieldPath,
  ): DecimalNumber {
    return reduce(
      itemList,
      (pre, curItem) => {
        const receivedQuantity = new DecimalNumber(
          curItem?.[formFieldPath.quantityReceived] || 0,
        );
        const receivedUnitCost = new DecimalNumber(
          curItem?.[formFieldPath.receivedUnitCost] || 0,
        );

        return pre.add(receivedQuantity.mul(receivedUnitCost));
      },
      new DecimalNumber(0),
    );
  }

  /**
   * build normal rows
   *
   * @param items base inventory items
   * @param formFieldPath form field path mapping
   * @returns normal rows data, if items is empty, will return null
   */
  public buildNormalRows(
    items: IGridFlatInventoryItemVenue[],
    formFieldPath: IOrderFormFieldPath,
  ): IGridFlatInventoryItemVenueTotal[] {
    return chain(items)
      .groupBy(formFieldPath.glAccountName)
      .map((groupedItemList, glAccountName) => {
        return <IGridFlatInventoryItemVenueTotal>{
          name: glAccountName,
          count: this.getSubTotal(
            groupedItemList,
            formFieldPath.initialItemCount,
          ),
          quantity_received: this.getSubTotal(
            groupedItemList,
            formFieldPath.itemCount,
          ),
          total_cost: this.getTotalCost(groupedItemList, {
            itemCountField: formFieldPath.initialItemCount,
            itemCostField: formFieldPath.initialItemCost,
          }),
          received_total_cost: this.getTotalCost(groupedItemList, {
            itemCountField: formFieldPath.itemCount,
            itemCostField: formFieldPath.itemCost,
          }),
        };
      })
      .filter((item) => isNumber(item.count))
      .value();
  }

  /**
   * build pinned row Data
   *
   * @param baseOnRows pinned row will combine all base on rows
   * @param formFieldPath form field path mapping
   * @returns pinned row data, if baseOnRows empty will return null
   */
  public buildPinnedData(
    baseOnRows: IGridFlatInventoryItemVenueTotal[],
    formFieldPath: IOrderFormFieldPath,
  ): [IGridFlatInventoryItemVenueTotal] {
    if (isEmpty(baseOnRows)) {
      // all rows empty, no pinned data
      return null;
    }

    const totalCount = this.getSubTotal(baseOnRows, formFieldPath.itemCount);
    const totalCost = this.getSubTotal(baseOnRows, formFieldPath.totalCost);
    const receivedCount = this.getSubTotal(
      baseOnRows,
      formFieldPath.quantityReceived,
    );
    const receivedCost = this.getSubTotal(
      baseOnRows,
      formFieldPath.receivedTotalCost,
    );
    const pinnedBottomData: IGridFlatInventoryItemVenueTotal = {
      name: this.i18nService.instant(I18n.Common.Totals),
      count: totalCount,
      total_cost: totalCost,
      quantity_received: receivedCount,
      received_total_cost: receivedCost,
    };
    this.totalItemData$.next(pinnedBottomData);

    return [pinnedBottomData];
  }

  /**
   * get sub total of data
   *
   * @param items inventory item or inventory total item
   * @param targetPath field path which need to calc sub total, if not exist or isNaN will use zero to replace it.
   * @returns items target field sub total
   */
  public getSubTotal(
    items: IGridFlatInventoryItemVenue[] | IGridFlatInventoryItemVenueTotal[],
    targetPath: string,
  ): DecimalNumber {
    const hasInput = some(
      items,
      (item) => !isNaN(parseFloat(item?.[targetPath])),
    );
    if (!hasInput) return null;

    return reduce(
      items,
      (
        pre,
        cur: IGridFlatInventoryItemVenue | IGridFlatInventoryItemVenueTotal,
      ) => {
        const curNumberStr = get(cur, targetPath, 0);
        const curNumber = parseFloat(`${curNumberStr}`);

        return pre.add(isNaN(curNumber) ? 0 : curNumberStr);
      },
      new DecimalNumber(0),
    );
  }

  public getTotalCost(
    itemList: IGridFlatInventoryItemVenue[],
    config: ITotalCostFieldConfig,
  ): DecimalNumber {
    return reduce(
      itemList,
      (pre, curItem) => {
        const itemCount = new DecimalNumber(
          curItem?.[config.itemCountField] || 0,
        );
        const itemCost = new DecimalNumber(
          curItem?.[config.itemCostField] || 0,
        );

        return pre.add(itemCount.mul(itemCost));
      },
      new DecimalNumber(0),
    );
  }

  /**
   * get from row class rules
   * data-pinned-bottom: for bottom pinned row style
   *
   * @returns style build model
   */
  public getFormRowClassRules(): IGridRowOrCellClassRule {
    return {
      'data-pinned-bottom': (params: CellClassParams): boolean =>
        params.node.isRowPinned(),
    };
  }

  /**
   * update list grid data by totalsData
   *
   * @param totalsData totalsData
   */
  public updateListGridData(
    totalsData: IGridInventoryItemVenueTotalFormData,
  ): void {
    if (isNil(totalsData)) return null;
    this.listGrid?.forceUpdateXuxGridItems(totalsData.normalRows);
    this.listGrid?.xuxGridComponent?.gridApi?.setPinnedBottomRowData(
      totalsData.pinnedBottomData,
    );
  }
}
